import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, TextField, Popover, Paper } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTimeline, TOOL_MODES, ROW_HEIGHT } from './TimelineProvider';
import TimelineNote from './TimelineNote';
import useThemeStyles from '../../hooks/useThemeStyles';

// Height of the sticky chapter header
const CHAPTER_HEADER_HEIGHT = 40;

// Default dimensions for new notes
const DEFAULT_NOTE_WIDTH = 200;
const DEFAULT_NOTE_HEIGHT = 200;

/**
 * TimelineCanvas - Infinite 2D scrollable canvas with timeline notes and chapter borders
 */
const TimelineCanvas = () => {
  const { colors, isDark } = useThemeStyles();
  const {
    notes,
    chapterBorders,
    showChapterBorders,
    viewState,
    setPan,
    setZoom,
    toolMode,
    setToolMode,
    addNote,
    // addBorder,
    moveBorder,
    updateBorder,
    deleteBorder,
    selectedBorderId,
    setSelectedBorderId,
  } = useTimeline();

  const containerRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [draggingBorderId, setDraggingBorderId] = useState(null);
  const [editingBorderId, setEditingBorderId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [labelPopoverAnchor, setLabelPopoverAnchor] = useState(null);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const borderDragStartRef = useRef({ x: 0, borderX: 0 });
  const rafPanRef = useRef(null);

  // Phantom note state for placement mode
  const [phantomNotePos, setPhantomNotePos] = useState(null);

  // Phantom border position for dragging preview
  const [phantomBorderX, setPhantomBorderX] = useState(null);

  // Local pan state for smooth panning
  const [localPan, setLocalPan] = useState(null);
  const { panX: reduxPanX, panY: reduxPanY, zoom } = viewState;
  const panX = localPan?.panX ?? reduxPanX;
  const panY = localPan?.panY ?? reduxPanY;

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (screenX, screenY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      return {
        x: (screenX - rect.left - panX) / zoom,
        y: (screenY - rect.top - panY) / zoom,
      };
    },
    [panX, panY, zoom]
  );

  // Constrain panY so users can't scroll above y=0 (the ceiling)
  const constrainPan = useCallback((newPanX, newPanY) => {
    // panY <= 0 means the canvas top (y=0) can't go below the viewport top
    const constrainedPanY = Math.min(0, newPanY);
    return { panX: newPanX, panY: constrainedPanY };
  }, []);

  // Handle wheel for horizontal scrolling (or zoom with Ctrl)
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      // Ctrl + scroll = zoom
      if (e.ctrlKey || e.metaKey) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.max(0.1, Math.min(5, zoom * delta));

        const zoomRatio = newZoom / zoom;
        const newPanX = mouseX - (mouseX - panX) * zoomRatio;
        const newPanY = mouseY - (mouseY - panY) * zoomRatio;

        // Apply ceiling constraint
        const constrained = constrainPan(newPanX, newPanY);
        setPan(constrained.panX, constrained.panY);
        setZoom(newZoom);
        return;
      }

      // Normal scroll = horizontal pan
      const scrollAmount = e.deltaY * 1.5; // Adjust multiplier for scroll speed
      const newPanX = panX - scrollAmount;

      // Apply constraint (keeps panY unchanged)
      const constrained = constrainPan(newPanX, panY);
      setPan(constrained.panX, constrained.panY);
    },
    [panX, panY, zoom, setPan, setZoom, constrainPan]
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e) => {
      const { target } = e;

      // Skip if clicking on a note
      if (target.closest && target.closest('[data-timeline-note]')) {
        return;
      }

      const canvasPos = screenToCanvas(e.clientX, e.clientY);

      // Handle note placement mode
      if (toolMode === TOOL_MODES.PLACE_NOTE) {
        // Snap Y to row
        const snappedY = Math.max(0, Math.round(canvasPos.y / ROW_HEIGHT) * ROW_HEIGHT);
        addNote({
          x: canvasPos.x,
          y: snappedY,
        });
        // Return to cursor mode after placing
        setToolMode(TOOL_MODES.CURSOR);
        setPhantomNotePos(null);
        return;
      }

      if (toolMode === TOOL_MODES.PAN || e.button === 1) {
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX,
          panY,
        };
        e.preventDefault();
        return;
      }

      if (toolMode === TOOL_MODES.CURSOR) {
        // Deselect border if clicking on empty space
        setSelectedBorderId(null);

        // Double click to create note
        if (e.detail === 2) {
          addNote({
            x: canvasPos.x,
            y: canvasPos.y,
          });
        }
      }
    },
    [toolMode, panX, panY, screenToCanvas, addNote, setSelectedBorderId, setToolMode]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      // Update phantom note position in placement mode
      if (toolMode === TOOL_MODES.PLACE_NOTE) {
        const canvasPos = screenToCanvas(e.clientX, e.clientY);
        // Snap Y to row
        const snappedY = Math.max(0, Math.round(canvasPos.y / ROW_HEIGHT) * ROW_HEIGHT);
        setPhantomNotePos({ x: canvasPos.x, y: snappedY });
      }

      // Handle panning
      if (isPanning) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        const newPanX = panStartRef.current.panX + dx;
        const newPanY = panStartRef.current.panY + dy;

        // Apply ceiling constraint
        const constrained = constrainPan(newPanX, newPanY);

        if (rafPanRef.current) {
          cancelAnimationFrame(rafPanRef.current);
        }
        rafPanRef.current = requestAnimationFrame(() => {
          setLocalPan(constrained);
          rafPanRef.current = null;
        });
      }
      // Border dragging is handled via document-level events in handleBorderMouseDown
    },
    [isPanning, constrainPan, toolMode, screenToCanvas]
  );

  // Handle mouse up (for panning only - border dragging uses document-level handlers)
  const handleMouseUp = useCallback(() => {
    if (rafPanRef.current) {
      cancelAnimationFrame(rafPanRef.current);
      rafPanRef.current = null;
    }
    if (localPan) {
      setPan(localPan.panX, localPan.panY);
      setLocalPan(null);
    }
    setIsPanning(false);
  }, [localPan, setPan]);

  // Ref to track current phantom position for document-level handlers
  const phantomBorderXRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    phantomBorderXRef.current = phantomBorderX;
  }, [phantomBorderX]);

  // Constrain border position to prevent overlapping with adjacent borders
  const constrainBorderPosition = useCallback(
    (borderId, newX) => {
      // Sort borders by x position
      const sortedBorders = [...chapterBorders].sort((a, b) => a.x - b.x);
      const currentIndex = sortedBorders.findIndex((b) => b.id === borderId);

      if (currentIndex === -1) return newX;

      // Get adjacent borders
      const prevBorder = currentIndex > 0 ? sortedBorders[currentIndex - 1] : null;
      const nextBorder =
        currentIndex < sortedBorders.length - 1 ? sortedBorders[currentIndex + 1] : null;

      // Minimum spacing between borders (to prevent overlap)
      const MIN_SPACING = 10;

      // Constrain to be between previous and next borders
      let constrainedX = newX;
      if (prevBorder) {
        constrainedX = Math.max(constrainedX, prevBorder.x + MIN_SPACING);
      }
      if (nextBorder) {
        constrainedX = Math.min(constrainedX, nextBorder.x - MIN_SPACING);
      }

      return constrainedX;
    },
    [chapterBorders]
  );

  // Handle border mouse down - use document-level events for dragging
  const handleBorderMouseDown = useCallback(
    (e, borderId) => {
      if (toolMode !== TOOL_MODES.CURSOR) return;

      e.stopPropagation();
      e.preventDefault();

      setSelectedBorderId(borderId);
      setDraggingBorderId(borderId);

      const border = chapterBorders.find((b) => b.id === borderId);
      const startX = e.clientX;
      const startBorderX = border?.x || 0;
      borderDragStartRef.current = {
        x: startX,
        borderX: startBorderX,
      };

      // Use document-level events for smooth dragging from anywhere
      const handleDocumentMouseMove = (moveEvent) => {
        const dx = (moveEvent.clientX - startX) / zoom;
        const newX = startBorderX + dx;
        // Constrain to prevent overlapping
        const constrainedX = constrainBorderPosition(borderId, newX);
        setPhantomBorderX(constrainedX);
        phantomBorderXRef.current = constrainedX;
      };

      const handleDocumentMouseUp = (upEvent) => {
        // Prevent any other handlers from interfering
        upEvent.stopPropagation();
        upEvent.preventDefault();

        // Apply the move using the ref value (already constrained in mousemove)
        if (phantomBorderXRef.current !== null) {
          // Apply constraint one more time as safety measure
          const finalX = constrainBorderPosition(borderId, phantomBorderXRef.current);
          moveBorder(borderId, finalX);
        }
        setPhantomBorderX(null);
        phantomBorderXRef.current = null;
        setDraggingBorderId(null);
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp, true);
        document.removeEventListener('mouseleave', handleDocumentMouseUp);
      };

      // Use capture phase for mouseup to ensure it fires before other handlers
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp, true);
      // Also handle mouseleave in case mouse leaves window
      document.addEventListener('mouseleave', handleDocumentMouseUp);
    },
    [toolMode, chapterBorders, setSelectedBorderId, zoom, moveBorder, constrainBorderPosition]
  );

  // Handle border label edit
  const handleBorderDoubleClick = useCallback(
    (e, borderId) => {
      e.stopPropagation();
      const border = chapterBorders.find((b) => b.id === borderId);
      if (border) {
        setEditingBorderId(borderId);
        setEditLabel(border.label || '');
        setLabelPopoverAnchor(e.currentTarget);
      }
    },
    [chapterBorders]
  );

  const handleLabelSave = useCallback(() => {
    if (editingBorderId) {
      updateBorder(editingBorderId, { label: editLabel });
      setEditingBorderId(null);
      setLabelPopoverAnchor(null);
    }
  }, [editingBorderId, editLabel, updateBorder]);

  // Sort chapter borders by x position for rendering
  const sortedChapterBorders = useMemo(() => {
    return [...chapterBorders].sort((a, b) => a.x - b.x);
  }, [chapterBorders]);

  // Attach wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Cursor style
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (draggingBorderId) return 'ew-resize';
    switch (toolMode) {
      case TOOL_MODES.PAN:
        return 'grab';
      case TOOL_MODES.PLACE_NOTE:
        return 'crosshair';
      default:
        return 'default';
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBorderId) {
        e.preventDefault();
        deleteBorder(selectedBorderId);
      }
      if (e.key === 'Escape') {
        // Cancel note placement mode
        if (toolMode === TOOL_MODES.PLACE_NOTE) {
          setToolMode(TOOL_MODES.CURSOR);
          setPhantomNotePos(null);
        }
        setSelectedBorderId(null);
        setEditingBorderId(null);
        setLabelPopoverAnchor(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedBorderId, deleteBorder, setSelectedBorderId, toolMode, setToolMode]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.primary,
      }}
    >
      {/* Sticky Chapter Header Row */}
      {showChapterBorders && sortedChapterBorders.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            height: CHAPTER_HEADER_HEIGHT,
            minHeight: CHAPTER_HEADER_HEIGHT,
            backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(250, 250, 250, 0.95)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            overflow: 'hidden',
            zIndex: 200,
            flexShrink: 0,
          }}
        >
          {/* Chapter labels - positioned based on panX */}
          {sortedChapterBorders.map((border, index) => {
            const isDraggingThis = draggingBorderId === border.id;
            // Use phantom position for the border being dragged
            const displayX = isDraggingThis && phantomBorderX !== null ? phantomBorderX : border.x;
            const screenX = displayX * zoom + panX;
            const isSelected = selectedBorderId === border.id;
            // Use the chapter's stored width, or default to 400
            const chapterWidth = border.width || 400;
            const sectionWidth = chapterWidth * zoom;

            // Compute colors based on selection and theme
            const getBorderColor = () => {
              if (isDraggingThis) return isDark ? '#60a5fa' : '#2563eb';
              if (isSelected) return isDark ? '#60a5fa' : '#2563eb';
              return isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)';
            };
            const getBackgroundColor = () => {
              if (isDraggingThis) {
                return isDark ? 'rgba(96, 165, 250, 0.2)' : 'rgba(37, 99, 235, 0.15)';
              }
              if (isSelected) {
                return isDark ? 'rgba(96, 165, 250, 0.15)' : 'rgba(37, 99, 235, 0.1)';
              }
              return 'transparent';
            };
            const getHoverBackground = () => {
              return isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
            };
            const getTextColor = () => {
              if (isDraggingThis) return isDark ? '#60a5fa' : '#2563eb';
              if (isSelected) return isDark ? '#60a5fa' : '#2563eb';
              return isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
            };
            const getBorderStyle = () => {
              if (isDraggingThis) return 'dashed';
              if (isSelected) return 'solid';
              return 'dotted';
            };

            return (
              <Box
                key={border.id}
                onMouseDown={(e) => handleBorderMouseDown(e, border.id)}
                onDoubleClick={(e) => handleBorderDoubleClick(e, border.id)}
                sx={{
                  position: 'absolute',
                  left: screenX,
                  top: 0,
                  height: '100%',
                  width: Math.max(sectionWidth, 100),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  cursor: 'ew-resize',
                  borderLeft: `2px ${getBorderStyle()} ${getBorderColor()}`,
                  backgroundColor: getBackgroundColor(),
                  transition: isDraggingThis ? 'none' : 'background-color 0.2s',
                  opacity: isDraggingThis ? 0.9 : 1,
                  '&:hover': {
                    backgroundColor: getHoverBackground(),
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    marginLeft: '1px', // Slight offset to account for border line
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: isSelected ? 600 : 500,
                      color: getTextColor(),
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 'calc(100% - 40px)', // Leave room for edit button
                      userSelect: 'none',
                      textAlign: 'center',
                      fontSize: '0.7rem', // Make it smaller
                    }}
                  >
                    {border.label || `Chapter ${index + 1}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: getTextColor(),
                      userSelect: 'none',
                      textAlign: 'center',
                      fontSize: '0.5rem', // Really small
                      opacity: 0.6,
                      lineHeight: 1,
                      mt: 0.25,
                    }}
                  >
                    Chapter {index + 1}
                  </Typography>
                </Box>

                {/* Selected chapter controls */}
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 4,
                      display: 'flex',
                      gap: 0.25,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingBorderId(border.id);
                        setEditLabel(border.label || '');
                        setLabelPopoverAnchor(e.currentTarget);
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Main Canvas Area */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          flex: 1,
          overflow: 'hidden',
          cursor: getCursor(),
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Chapter borders vertical lines */}
        {showChapterBorders && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {chapterBorders.map((border) => {
              const isDraggingThis = draggingBorderId === border.id;
              // Use phantom position for the border being dragged, otherwise use actual position
              const displayX =
                isDraggingThis && phantomBorderX !== null ? phantomBorderX : border.x;
              const screenX = displayX * zoom + panX;
              const isSelected = selectedBorderId === border.id;

              // Get colors based on state
              const getBorderStyle = () => {
                if (isDraggingThis) return 'dashed';
                if (isSelected) return 'solid';
                return 'dotted';
              };
              const getBorderColor = () => {
                if (isDraggingThis) return isDark ? '#60a5fa' : '#2563eb';
                if (isSelected) return isDark ? '#60a5fa' : '#2563eb';
                return isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)';
              };

              return (
                <Box
                  key={border.id}
                  sx={{
                    position: 'absolute',
                    left: screenX,
                    top: 0,
                    height: '100%',
                    pointerEvents: 'auto',
                    zIndex: isSelected || isDraggingThis ? 100 : 10,
                    opacity: isDraggingThis ? 0.8 : 1,
                  }}
                >
                  {/* Border line */}
                  <Box
                    onMouseDown={(e) => handleBorderMouseDown(e, border.id)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      width: isSelected || isDraggingThis ? 4 : 2,
                      marginLeft: isSelected || isDraggingThis ? -2 : -1,
                      borderLeft: `2px ${getBorderStyle()} ${getBorderColor()}`,
                      cursor: 'ew-resize',
                      transition: isDraggingThis ? 'none' : 'border-color 0.2s',
                      '&:hover': {
                        borderColor: isDark ? '#60a5fa' : '#2563eb',
                        borderStyle: 'solid',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -8,
                        right: -8,
                        top: 0,
                        bottom: 0,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {/* Notes layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transformOrigin: '0 0',
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            willChange: 'transform',
          }}
        >
          {notes.map((note) => (
            <TimelineNote key={note._id || note.id || note.noteId} note={note} />
          ))}

          {/* Phantom note for placement preview */}
          {toolMode === TOOL_MODES.PLACE_NOTE && phantomNotePos && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                left: phantomNotePos.x,
                top: phantomNotePos.y,
                width: DEFAULT_NOTE_WIDTH,
                height: DEFAULT_NOTE_HEIGHT,
                backgroundColor: isDark ? 'rgba(254, 243, 199, 0.6)' : 'rgba(254, 243, 199, 0.8)',
                border: `2px dashed ${isDark ? '#60a5fa' : '#2563eb'}`,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
                  fontStyle: 'italic',
                }}
              >
                Click to place note
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Zoom indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.6)',
            color: isDark ? colors.text.primary : 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          {Math.round(zoom * 100)}%
        </Box>
      </Box>

      {/* Label edit popover */}
      <Popover
        open={Boolean(labelPopoverAnchor)}
        anchorEl={labelPopoverAnchor}
        onClose={() => {
          setLabelPopoverAnchor(null);
          setEditingBorderId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Chapter Label
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            placeholder="Enter chapter name..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleLabelSave();
              } else if (e.key === 'Escape') {
                setLabelPopoverAnchor(null);
                setEditingBorderId(null);
              }
            }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <IconButton
              size="small"
              onClick={() => {
                setLabelPopoverAnchor(null);
                setEditingBorderId(null);
              }}
            >
              Cancel
            </IconButton>
            <IconButton size="small" color="primary" onClick={handleLabelSave}>
              Save
            </IconButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default TimelineCanvas;
